import { Component, type ErrorInfo, type ReactNode } from "react";
import { RADIUS } from "./card";
import { C, FU, T, W } from "./tokens";

interface Props {
  resetKey?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** Keeps a crash inside one section from unmounting the whole dashboard.
    React drops the entire tree on an uncaught render error, which the reader
    sees as a blank page; this shows what failed and a way back. */
export class SectionErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("section render failed", error, info.componentStack);
  }

  override componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          margin: "48px auto",
          maxWidth: 520,
          padding: "28px 32px",
          borderRadius: RADIUS.xl,
          border: `1px solid ${C.border}`,
          background: C.surface,
          color: C.textHi,
          fontFamily: FU,
        }}
      >
        <div style={{ fontSize: T.head, fontWeight: W.semibold, marginBottom: 8 }}>This section could not be shown</div>
        <div style={{ fontSize: T.body, color: C.textMd, marginBottom: 18 }}>
          Something went wrong while drawing it. Reloading the page usually fixes this.
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            fontFamily: FU,
            fontSize: T.body,
            fontWeight: W.medium,
            padding: "8px 16px",
            borderRadius: RADIUS.md,
            border: "none",
            background: C.accent,
            color: C.onAccent,
            cursor: "pointer",
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
